import { CONTENT } from '../content/runtime.js';
import type { EconomyData, StationData, TerminalEconomyData } from "./types.js";
type Rect = { x: number; y: number; w: number; h: number };
import { masteryFacts, masteryMissing } from "./act2.js";
/**
 * Career ladder. Each airport is a fresh terminal on the same floor plan; departments outside the
 * current airport are fenced off. Planes belong to visiting airlines until you found your own airline
 * at Pacific Gateway, your second hub. Earlier airports stay in your portfolio and send passive income.
 * All names and numbers are fictional game tuning.
 */
export interface AirportTier {
    name: string;
    code: string;
    size: string;
    /** Department slots (CONFIG.stages indices) that exist at this airport. */
    stations: number[];
    vending: number[];
    maxLanes: number;
    maxGates: number;
    /** Visiting-airline aircraft that serve this airport before you run your own airline. */
    aircraft: string;
    seats: number;
    fare: number;
    /** Frames an aircraft spends away before the next one arrives at the gate. */
    turnaround: number;
    /** A partial visiting load closes this many frames after its scheduled departure. */
    boardingWindow: number;
    incomeMult: number;
    costMult: number;
    /** Bills per minute this airport sends once you have moved on to a bigger one. */
    passivePerMinute: number;
    /** Requirements to move on. */
    graduate: { earned: number; flights: number; gates: number };
    /** Right-hand edge of the walkable, visible terminal. */
    maxX: number;
    chairs: boolean;
    /** Airstairs out on the apron, or jet bridges from the gate door. */
    boarding: 'stairs' | 'bridge';
    /** Gate-dependent geometry when this airport has its own gate row (content `gateRow`); otherwise the shared plan. */
    gateLayout?: { GATES: { x: number; y: number }[]; GATE_WALLS: Rect[]; BOUNDARIES: Rect[]; OBSTACLES: Rect[]; DIVIDER: Rect[]; OUTLINE?: number[]; WALL_RUNS?: number[][];
        /** Areas behind hoarding until a gate on their walls (or in an area reached through them, `parent`) is bought. */
        EXPANSIONS?: { ring: number[]; gates: number[]; doors: Rect[]; parent: number; hoarding: Rect[] }[] };
}
export const AIRPORTS = CONTENT.airports as AirportTier[];
/** Index into AIRPORTS for this career run. Runs past the last airport keep the last airport's rules. */
export function tierIndex(e: Pick<EconomyData, "run">): number { return Math.max(0, Math.min(AIRPORTS.length - 1, (e.run || 1) - 1)); }
export function tier(e: Pick<EconomyData, "run">): AirportTier { return AIRPORTS[tierIndex(e)]; }
export function stationAllowed(e: Pick<EconomyData, "run">, index: number): boolean { return tier(e).stations.includes(index); }
export function vendingAllowed(e: Pick<EconomyData, "run">, index: number): boolean { return tier(e).vending.includes(index); }
/** Mission index at Pacific Gateway after which you run Sky Harbor Airways. Validated with the authored mission ladder at build time. */
export const AIRLINE_TIER = CONTENT.operations.airlineTier;
const airlineMission = CONTENT.operations.airlineMission;
/** The PGW ladder step that founds the airline; the Founding skips past it on arrival from Metro. */
export const AIRLINE_MISSION = airlineMission;
/** Your own airline: planes need hired pilots and route planning opens. */
export function airlineMode(e: Pick<EconomyData, "run" | "mission">): boolean {
    const t = tierIndex(e);
    return t > AIRLINE_TIER || (t === AIRLINE_TIER && e.mission > airlineMission);
}
export function portfolio(e: Pick<EconomyData, "run">): AirportTier[] { return AIRPORTS.slice(0, tierIndex(e)); }
/** Flat legacy income from earlier airports; managed airports earn through L1 instead and are excluded. */
export function passivePerMinute(e: Pick<EconomyData, "run">, prestige: number, managed: readonly string[] = []): number {
    return Math.floor(portfolio(e).filter(a => !managed.includes(a.code)).reduce((n, a) => n + a.passivePerMinute, 0) * (1 + prestige * 0.1));
}
export function nextAirport(e: Pick<EconomyData, "run">): AirportTier { return AIRPORTS[Math.min(AIRPORTS.length - 1, tierIndex(e) + 1)]; }
/** What still blocks moving to the next airport; empty when ready. */
export function graduationMissing(e: TerminalEconomyData, ss: StationData[]): string[] {
    const t = tier(e), out: string[] = [];
    const closed = t.stations.filter(i => !ss[i]?.unlocked).length;
    if (closed) out.push(`Open ${closed} more department${closed === 1 ? "" : "s"}`);
    if ((ss[6]?.lanes ?? 1) < t.graduate.gates) out.push(`Run ${t.graduate.gates} gates`);
    // Act II airports are mastered by how they run (MGT-023), not by lifetime earnings or departure counts.
    if (t.code === 'BVM' || t.code === 'MTR') return [...out, ...masteryMissing(masteryFacts(e as never))];
    if (e.flights < t.graduate.flights) out.push(`${t.graduate.flights - e.flights} more departures`);
    if (e.earned < t.graduate.earned) out.push(`Earn ${Math.ceil(t.graduate.earned - e.earned).toLocaleString("en-US")} more bills`);
    return out;
}
/** Reputation granted for moving on; zero means not ready. */
export function graduationAward(e: TerminalEconomyData, ss: StationData[]): number {
    if (graduationMissing(e, ss).length) return 0;
    return Math.max(1, Math.floor(Math.sqrt(e.earned / tier(e).graduate.earned) * 3));
}
